import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { ApolloError, useLazyQuery, useQuery } from '@apollo/client';
import {
    CurrentPrices,
    PriceChangeRequestAction,
    Request,
    Store,
} from '../codegen/graphql';
import {
    GET_AD_ZONE_STORES_FOR_REQUEST,
    GET_PRICE_CHANGE_REQUEST_TABLE_ROW,
} from '../apollo/queries/priceChangeRequest';
import { GET_ACTIONS_FOR_REQUEST } from '../apollo/queries/priceChangeRequestActions';
import { GET_CURRENT_PRICES } from '../apollo/queries/product';
import {
    isPnlStore,
    isPnlStoreTradingAsCashbuild,
} from '../helpers/storeHelpers';

function pickCurrentPricesStoreId(
    requestStoreId: string | null | undefined,
    adZoneStores: Store[],
): string | null {
    if (requestStoreId) {
        return requestStoreId;
    }
    const cashbuildStore = adZoneStores.find(
        store => !isPnlStore(store) || isPnlStoreTradingAsCashbuild(store),
    );
    if (cashbuildStore) {
        return cashbuildStore.id;
    }
    return adZoneStores[0]?.id ?? null;
}

export type RequestDrawerOpenOptions = {
    /** When true, Accept / Decline / interim controls are hidden in the drawer. */
    hideActions?: boolean;
    /** Same as hideActions — use when opening a row from pricing batch grouping cards. */
    fromGrouping?: boolean;
};

interface RequestDrawerContextType {
    flatRequest: Request | null;
    flatLoading: boolean;

    adZoneStores: Store[];
    adZoneLoading: boolean;
    adZoneError?: ApolloError;

    actions: PriceChangeRequestAction[];
    actionsLoading: boolean;
    actionsError?: ApolloError;

    currentPrices: CurrentPrices | undefined;
    currentPricesLoading: boolean;
    currentPricesError?: ApolloError;

    hasSharedDataError: boolean;
    retrySharedQueries: () => void;

    open: (
        row: Request | null,
        requestId?: string,
        options?: RequestDrawerOpenOptions,
    ) => void;
    close: () => void;
    isOpen: boolean;
    requestId: string | null;
    hideDrawerActions: boolean;

    drawerOpenedAt: number | null;
}

const RequestDrawerContext = createContext<RequestDrawerContextType>({
    flatRequest: null,
    flatLoading: false,
    adZoneStores: [],
    adZoneLoading: false,
    adZoneError: undefined,
    actions: [],
    actionsLoading: false,
    actionsError: undefined,
    currentPrices: undefined,
    currentPricesLoading: false,
    currentPricesError: undefined,
    hasSharedDataError: false,
    retrySharedQueries: () => {},
    open: () => {},
    close: () => {},
    isOpen: false,
    requestId: null,
    hideDrawerActions: false,
    drawerOpenedAt: null,
});

type Props = Readonly<React.PropsWithChildren>;

export function RequestDrawerProvider({ children }: Props) {
    const [flatRequest, setFlatRequest] = useState<Request | null>(null);
    const [requestId, setRequestId] = useState<string | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [hideDrawerActions, setHideDrawerActions] = useState(false);

    const drawerOpenedAtRef = useRef<number | null>(null);
    const currentRequestIdRef = useRef<string | null>(null);
    currentRequestIdRef.current = requestId;

    const [fetchTableRow, { loading: flatLoading }] = useLazyQuery(
        GET_PRICE_CHANGE_REQUEST_TABLE_ROW,
        {
            fetchPolicy: 'cache-and-network',
            onCompleted: data => {
                const row = data?.getPriceChangeRequestTableRow as
                    | Request
                    | undefined;
                if (
                    row &&
                    String(row.id) === String(currentRequestIdRef.current)
                ) {
                    setFlatRequest(row);
                }
            },
        },
    );

    const prevRequestIdRef = useRef<string | null>(null);
    const [isDataStale, setIsDataStale] = useState(false);

    if (prevRequestIdRef.current !== requestId) {
        prevRequestIdRef.current = requestId;
        if (requestId !== null) {
            setIsDataStale(true);
        }
    }

    const {
        data: adZoneData,
        loading: rawAdZoneLoading,
        error: adZoneError,
        refetch: refetchAdZone,
    } = useQuery(GET_AD_ZONE_STORES_FOR_REQUEST, {
        variables: {
            requestId: requestId!,
            productSku: flatRequest?.productSku,
        },
        skip: !requestId || !flatRequest?.productSku,
        fetchPolicy: 'cache-and-network',
    });

    const {
        data: actionsData,
        loading: rawActionsLoading,
        error: actionsError,
        refetch: refetchActions,
    } = useQuery(GET_ACTIONS_FOR_REQUEST, {
        variables: { requestId: requestId! },
        skip: !requestId,
        fetchPolicy: 'cache-and-network',
    });

    const adZoneStores: Store[] = useMemo(
        () =>
            isDataStale
                ? []
                : adZoneData?.getAdvertisingZoneStoresForRequest ?? [],
        [adZoneData, isDataStale],
    );

    const currentPricesStoreId = useMemo(
        () => pickCurrentPricesStoreId(flatRequest?.storeId, adZoneStores),
        [flatRequest?.storeId, adZoneStores],
    );

    const {
        data: currentPricesData,
        loading: rawCurrentPricesLoading,
        error: currentPricesError,
        refetch: refetchCurrentPrices,
    } = useQuery(GET_CURRENT_PRICES, {
        variables: {
            productSku: flatRequest?.productSku ?? '',
            storeId: currentPricesStoreId ?? '',
        },
        skip: !flatRequest?.productSku || !currentPricesStoreId,
        fetchPolicy: 'cache-and-network',
    });

    useEffect(() => {
        if (
            isDataStale &&
            requestId &&
            !rawAdZoneLoading &&
            !rawActionsLoading
        ) {
            setIsDataStale(false);
        }
    }, [isDataStale, requestId, rawAdZoneLoading, rawActionsLoading]);

    const adZoneLoading = rawAdZoneLoading || isDataStale;
    const actionsLoading = rawActionsLoading || isDataStale;
    const currentPricesLoading = rawCurrentPricesLoading || isDataStale;

    const actions: PriceChangeRequestAction[] = useMemo(
        () =>
            isDataStale
                ? []
                : actionsData?.getPriceChangeRequestActionsByPriceChangeRequestId ??
                  [],
        [actionsData, isDataStale],
    );

    const currentPrices: CurrentPrices | undefined = useMemo(
        () => (isDataStale ? undefined : currentPricesData?.getCurrentPrices),
        [currentPricesData, isDataStale],
    );

    const hasSharedDataError = !!(
        adZoneError ||
        actionsError ||
        currentPricesError
    );

    const retrySharedQueries = useCallback(() => {
        if (adZoneError && requestId) {
            refetchAdZone({
                requestId,
                productSku: flatRequest?.productSku,
            });
        }
        if (actionsError && requestId) {
            refetchActions({ requestId });
        }
        if (
            currentPricesError &&
            flatRequest?.productSku &&
            currentPricesStoreId
        ) {
            refetchCurrentPrices({
                productSku: flatRequest.productSku,
                storeId: currentPricesStoreId,
            });
        }
    }, [
        adZoneError,
        actionsError,
        currentPricesError,
        requestId,
        flatRequest,
        currentPricesStoreId,
        refetchAdZone,
        refetchActions,
        refetchCurrentPrices,
    ]);

    const open = useCallback(
        (
            row: Request | null,
            id?: string,
            options?: RequestDrawerOpenOptions,
        ) => {
            const resolvedId = id ?? row?.id ?? null;
            setRequestId(resolvedId);
            setIsOpen(true);
            setHideDrawerActions(
                Boolean(options?.hideActions) || Boolean(options?.fromGrouping),
            );
            drawerOpenedAtRef.current = Date.now();

            if (row) {
                setFlatRequest(row);
            } else {
                setFlatRequest(null);
            }

            if (resolvedId) {
                fetchTableRow({ variables: { id: resolvedId } });
            }
        },
        [fetchTableRow],
    );

    const close = useCallback(() => {
        setIsOpen(false);
        setRequestId(null);
        setFlatRequest(null);
        setHideDrawerActions(false);
        drawerOpenedAtRef.current = null;
    }, []);

    const drawerOpenedAt = drawerOpenedAtRef.current;

    const value = useMemo(
        () => ({
            flatRequest,
            flatLoading,
            adZoneStores,
            adZoneLoading,
            adZoneError,
            actions,
            actionsLoading,
            actionsError,
            currentPrices,
            currentPricesLoading,
            currentPricesError,
            hasSharedDataError,
            retrySharedQueries,
            open,
            close,
            isOpen,
            requestId,
            hideDrawerActions,
            drawerOpenedAt,
        }),
        [
            flatRequest,
            flatLoading,
            adZoneStores,
            adZoneLoading,
            adZoneError,
            actions,
            actionsLoading,
            actionsError,
            currentPrices,
            currentPricesLoading,
            currentPricesError,
            hasSharedDataError,
            retrySharedQueries,
            open,
            close,
            isOpen,
            requestId,
            hideDrawerActions,
            drawerOpenedAt,
        ],
    );

    return (
        <RequestDrawerContext.Provider value={value}>
            {children}
        </RequestDrawerContext.Provider>
    );
}

export const useRequestDrawer = () => {
    const context = useContext(RequestDrawerContext);

    if (!context) {
        throw new Error(
            'useRequestDrawer must be used within a RequestDrawerProvider',
        );
    }

    return context;
};
